<template>
  <v-card
    v-if="allReviews.length"
    :class="showFullList ? 'col-12 col-md-8 mx-auto' : 'col-12'"
    flat
    class="pa-0"
    color="transparent"
  >
    <!-- title of reviews -->
    <v-card-title class="px-0">
      <h2 class="homepageTitle text-break">
        {{ title }}
      </h2>
    </v-card-title>

    <v-card-text class="px-0">
      <!-- list of reviews -->
      <ItemGroup :items="getLatestFourReviews">
        <template v-slot:item="{ item }">
          <v-card elevation="1" class="pa-2 colorCard" height="100%" rounded >
            <v-card-text class="pa-1">
              <div class="text-h6 text--primary">{{ item.Name }}</div>
              <v-rating
                :value="item.Rating"
                color="yellow darken-3"
                background-color="yellow darken-3"
                empty-icon="star_outline"
                dense
                half-increments
                readonly
              />
              <div class="text--disabled text-caption px-1">
                {{ item.DateStr }}
              </div>
              <p class="mb-0 mt-2 text--primary">{{ item.Review }}</p>

              <!-- Response from partner -->
              <v-row v-if="item.Reviewd" align="center" class="grey lighten-3">
                <v-divider vertical />
                <v-col>
                  <v-row align="center">
                    <v-col cols="12" md="9" class="py-0 mb-n2">
                      <p class="font-weight-bold">
                        {{
                          $translate('RESPONSE_FROM') + ' ' + item.Response.Name
                        }}
                      </p>
                    </v-col>

                    <v-col cols="12" md="3" class="py-0 mb-n2">
                      <p class="text--disabled text-right">
                        {{ item.Response.DateStr }}
                      </p>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>
      </ItemGroup>

      <!-- see more reviews button -->
      <v-col
        v-if="!showFullList && this.allReviews.length > getReviewAmount"
        align="center"
      >
        <v-btn class="primary" @click="goToReviewPage()">
          {{ $translate('REVIEWS_SEE_MORE') }}
        </v-btn>
      </v-col>
    </v-card-text>
  </v-card>
</template>

<script>
import doRequest from '@/api/dssRequest';
import uiHelpers from '@/shared/ui_helpers';
import ItemGroup from '@/components/generalComponents/uiComponents/ItemGroup';

export default {
  name: 'Reviews',
  mixins: [uiHelpers],
  props: {
    showFullList: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: {
    ItemGroup,
  },
  computed: {
    title() {
      const pgInfo = this.$store.getters['Account/partnerGroupDetails'];

      if (
        pgInfo != null &&
        pgInfo.ReviewsTitle != null &&
        pgInfo.ReviewsTitle.trim() !== ''
      ) {
        return pgInfo.ReviewsTitle.trim();
      }
      return this.$translate('REVIEWS_TITLE');
    },
    getReviewAmount() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
          return 2;
        case 'md':
          return 3;
        case 'lg':
        case 'xl':
          return 4;
        default:
          return 0;
      }
    },
    getLatestFourReviews() {
      // sort the array by date (get the youngest review first)
      const sortedReviewsByDate = this.allReviews
        .slice()
        .sort((a, b) => b.DateStr - a.DateStr);
      let newReviewsList = sortedReviewsByDate;
      // if the showFullList is false show only the first four elements
      if (!this.showFullList) {
        // return only the first four elements
        const size = this.getReviewAmount;
        // get the first four elements of the array
        newReviewsList = sortedReviewsByDate.slice(0, size);
      }
      // return the sorted array
      return newReviewsList;
    },
  },
  data() {
    return {
      partnerPromotionId: null,
      maxRating: 5,
      allReviews: [],
    };
  },
  mounted() {
    this.init();
    this.initPrimaryColorWithOpacity();

  },
  methods: {
    initPrimaryColorWithOpacity() {
      const opacityHexValue = '26';
      const selector = document.querySelector(':root');
      selector.style.setProperty(
        '--primary-color-opacity1',
        `${this.$vuetify.theme.themes.light.primary}${opacityHexValue}`
      );
    },

    async init() {
      try {
        await this.getPartnerPromotions();
        this.getReviews();
      } catch (error) {
        console.error('Initialization failed: ', error);
      }
    },
    getPartnerPromotions() {
      const payload = {
        IncludeAllPartnersInPartnerGroup: false,
        EventTypes: ['PartnerPromotion'],
      };

      return new Promise((resolve, reject) => {
        doRequest(
          (response) => {
            const partnerPromotions = [];

            if (response.data.Events != null) {
              Object.entries(response.data.Events).forEach(([key, value]) =>
                partnerPromotions.push({ key, ...value })
              );
            }

            if (partnerPromotions.length > 0) {
              this.partnerPromotionId = partnerPromotions[0].EventId;
            } else {
              this.partnerPromotionId = null;
            }

            resolve();
          },
          () => {
            this.partnerPromotionId = null;
            reject();
          },
          'getEvents',
          'LocationsNode_ESE',
          payload,
          'GET'
        );
      });
    },
    getReviews() {
      if (!this.partnerPromotionId) return;

      const payload = {
        From: 0,
        Until: 10,
      };

      doRequest(
        (response) => {
          response.data.Reviews.forEach((review) => {
            review.Review = review.Review.replace(/\n/g, '<br>');
            review.Response.Response = review.Response.Response.replace(
              /\n/g,
              '<br>'
            );

            this.allReviews.push(review);
          });
          if (this.allReviews.length > 5) {
            this.allReviews = this.allReviews.slice(0, 5);
          }
        },
        () => {},
        'getReviews',
        this.partnerPromotionId,
        payload,
        'GET'
      );
    },
    getReviewColor(rating) {
      switch (rating) {
        case 1:
          return 'grey lighten-1';
        case 2:
          return 'grey lighten-1';
        case 3:
          return 'yellow lighten-3';
        case 4:
          return 'lime lighten-2';
        case 5:
          return 'green lighten-2';
        default:
          return '';
      }
    },
    checkForReviews() {
      // check if the reviews list is not empty and you are on the review page
      if (this.reviews.length === 0 && this.showFullList) {
        this.goToPage('Homepage');
      }
    },
    goToReviewPage() {
      // route to the review page
      this.goToPage('Reviews', {
        reviews: this.reviews,
        showFullList: true,
      });
    },
  },
};
</script>
<style scoped>
.colorCard{
  background-color: var(--primary-color-opacity1);
  
}
</style>